import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Forced first-login password change.
 *
 * Reached automatically when the authenticated user has
 * `must_change_password === true` (see ProtectedRoute redirect).  Also
 * reachable voluntarily at /change-password to rotate a password.
 */
export default function ChangePassword() {
  const { user, refreshUser, logout } = useAuth();
  const nav = useNavigate();

  const [current, setCurrent] = useState("");
  const [pwd, setPwd]         = useState("");
  const [pwd2, setPwd2]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pwd.length < 12) { setErr("New password must be at least 12 characters."); return; }
    if (pwd !== pwd2)    { setErr("New passwords don't match."); return; }
    if (pwd === current) { setErr("New password must differ from the current one."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password",
        { current_password: current, new_password: pwd });
      toast.success("Password updated");
      await refreshUser?.();
      nav("/admin/matters?status=new", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md bg-white border border-zinc-200" data-testid="change-password-page">
        <div className="bg-zinc-900 text-white px-6 py-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · Account security</div>
              <div className="text-sm font-medium">
                {user?.must_change_password ? "Set a new password to continue" : "Change password"}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {user?.must_change_password && (
            <div className="text-[12px] text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2">
              You're using a temporary password assigned by your admin.
              Please choose a new password to continue.
            </div>
          )}

          <label className="block">
            <span className="text-xs text-zinc-500">Current password</span>
            <Input type="password" value={current} required autoFocus
                   onChange={(e) => setCurrent(e.target.value)}
                   className="mt-1 rounded-none"
                   data-testid="current-password-input"/>
          </label>

          <label className="block">
            <span className="text-xs text-zinc-500">New password <span className="text-zinc-400">(min 12 characters)</span></span>
            <Input type="password" value={pwd} required
                   onChange={(e) => setPwd(e.target.value)}
                   className="mt-1 rounded-none"
                   data-testid="new-password-input"/>
          </label>

          <label className="block">
            <span className="text-xs text-zinc-500">Confirm new password</span>
            <Input type="password" value={pwd2} required
                   onChange={(e) => setPwd2(e.target.value)}
                   className="mt-1 rounded-none"
                   data-testid="confirm-password-input"/>
          </label>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2" data-testid="change-password-error">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5"/>{err}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" disabled={busy}
                    className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                    data-testid="change-password-submit">
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/>Updating…</>
                : "Update password"}
            </Button>
            {!user?.must_change_password && (
              <Button type="button" variant="outline" className="rounded-none"
                      onClick={() => nav(-1)}>
                Cancel
              </Button>
            )}
            {user?.must_change_password && (
              <button type="button"
                      onClick={() => { logout(); nav("/login", { replace: true }); }}
                      className="text-xs text-zinc-500 hover:text-zinc-900 ml-auto">
                Sign out instead
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
